import { useModal } from "@ebay/nice-modal-react";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect } from "react";
import { Base64 } from "js-base64";

import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import { ConfigElement, ConfigInput, ConfigType } from "types/generated";
import { getDrawerFormFix } from "utils/css";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import Button from "ds/components/Button";
import useIntentProjectConfigAdd from "shared/IntentProject/useIntentProjectConfigAdd";
import FormDescriptionField from "components/Environment/components/FormItems/DescriptionField";
import FormIsSecretField from "components/Environment/components/FormItems/IsSecretField";
import useIntentProjectConfigUpdate from "shared/IntentProject/useIntentProjectConfigUpdate";
import { useCheckFileMaxSize } from "components/Environment/useCheckFileMaxSize";
import Banner from "ds/components/Banner";
import FormFileUploadEditorTabs, {
  FileUploadEditorTabsValues,
} from "components/Environment/components/FormItems/FileUploadEditorField";
import FormPathField from "components/Environment/components/FormItems/PathField";
import { fileToBase64 } from "utils/file";

type MountedFileFormValues = {
  id: string;
  description: string;
  fileBody: string;
  file: File | null;
  writeOnly: boolean;
};

type IntentProjectEnvironmentMountedFileFormDrawerProps = {
  intentProjectId: string;
  file?: ConfigElement;
};

const IntentProjectEnvironmentMountedFileFormDrawer =
  createDrawer<IntentProjectEnvironmentMountedFileFormDrawerProps>(({ intentProjectId, file }) => {
    const drawer = useModal();
    const isEdit = !!file;
    const initialTab = isEdit
      ? FileUploadEditorTabsValues.AddManually
      : FileUploadEditorTabsValues.Upload;

    const { addConfigToIntentProject, loading: addConfigLoading } = useIntentProjectConfigAdd();
    const { updateConfigToIntentProject, loading: updateConfigLoading } =
      useIntentProjectConfigUpdate();

    const { fileContent, exceedsFileSize, isFileBroken } = useCheckFileMaxSize(file?.value ?? "");

    const fileForm = useForm<MountedFileFormValues>({
      defaultValues: {
        id: file?.id ?? "",
        description: file?.description ?? "",
        writeOnly: file?.writeOnly ?? true,
        fileBody: fileContent,
        file: null,
      },
      shouldFocusError: true,
      mode: "onChange",
    });

    const {
      handleSubmit,
      formState: { isDirty },
      setValue,
    } = fileForm;

    useEffect(() => {
      if (!isFileBroken) {
        setValue("fileBody", fileContent);
      }
    }, [fileContent, isFileBroken, setValue]);

    const onSubmit = async (formData: MountedFileFormValues) => {
      const base64 = formData.file && (await fileToBase64(formData.file));
      const base64File = base64 ? base64.toString().split(",")[1] : "";
      const fileValue = formData.file ? base64File : Base64.encode(formData.fileBody);

      const configInput: ConfigInput = {
        id: formData.id,
        description: formData.description,
        writeOnly: formData.writeOnly,
        type: ConfigType.FileMount,
        value: fileValue,
        fileMode: null,
      };

      if (isEdit) {
        updateConfigToIntentProject(intentProjectId, configInput, () => {
          drawer.hide();
        });
      } else {
        addConfigToIntentProject(intentProjectId, configInput, () => {
          drawer.hide();
        });
      }
    };

    return (
      <DrawerForm isDirty={isDirty}>
        <FormProvider {...fileForm}>
          <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
            <DrawerHeader justify="between">
              <DrawerHeaderTitle title={isEdit ? "Edit file" : "Add file"} />
              <DrawerCloseIcon />
            </DrawerHeader>

            <DrawerBody gap="x-large">
              {isFileBroken && (
                <Banner variant="danger" title="We couldn't load the file.">
                  Please try again later
                </Banner>
              )}

              {!isFileBroken && (
                <>
                  <FormPathField name="id" disabled={isEdit} />
                  <FormIsSecretField
                    name="writeOnly"
                    description="Secret values are not accessible in the web GUI and through the API."
                  />
                  <FormDescriptionField name="description" minRows={4} noMargin />

                  <FormFileUploadEditorTabs
                    uploadName="file"
                    contentName="fileBody"
                    fullHeight
                    initialTab={initialTab}
                    exceedsFileSize={exceedsFileSize}
                  />
                </>
              )}
            </DrawerBody>
            <DrawerFooter>
              <DrawerFooterActions>
                <DrawerCancelButton />
                <Button
                  variant="primary"
                  type="submit"
                  disabled={addConfigLoading || updateConfigLoading}
                >
                  {isEdit ? "Save" : "Create"}
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </form>
        </FormProvider>
      </DrawerForm>
    );
  });

export const showIntentProjectEnvironmentMountedFileFormDrawer = createDrawerTrigger(
  IntentProjectEnvironmentMountedFileFormDrawer
);
